import { CarIcon, RabbitIcon } from 'lucide-react-native';
import { useState } from 'react';
import { YStack } from 'tamagui';

import { Input } from '../../components/inputs/Input';
import {
    RadioInputRow,
    RadioInputRowList,
} from '../../components/inputs/RadioInput';
import { SegmentedControl } from '../../components/segmented-controls/SegmentedControl';
import { SelectSingle } from '../../components/select/SelectSingle';
import {
    BodyLargeEmphasized,
    BodySmallRegular,
} from '../../components/text/Typography';

export const SelectSingleExample = () => {
    const [selectedOption, setSelectedOption] = useState<number | null>(null);

    const options = [
        {
            title: 'Default state',
        },
        {
            title: 'Option with description',
            description: 'This option includes additional context',
        },
        {
            title: 'Selected state',
            description: 'This option is currently selected',
        },
        {
            title: 'Disabled state',
            description: 'This option is disabled and cannot be selected',
            disabled: true,
        },
    ];

    return (
        <YStack gap={80}>
            <YStack gap={16}>
                <BodyLargeEmphasized>SelectSingle</BodyLargeEmphasized>
                <BodySmallRegular>
                    A simple select component with title and optional
                    description. Perfect for single selection scenarios with
                    clean, minimal design. Features three states: default,
                    selected, and disabled.
                </BodySmallRegular>
                <YStack maxWidth={450}>
                    <SelectSingle
                        options={options}
                        selectedIndex={selectedOption ?? 2}
                        onSelect={setSelectedOption}
                    />
                </YStack>
            </YStack>
            <RadioInputRowListExamples />
        </YStack>
    );
};

const RadioInputRowListExamples = () => {
    const [disabled, setDisabled] = useState(false);
    const [selectedStates, setSelectedStates] = useState([
        0, 1, 0, 1, 0, 1, 0, 1,
    ]);
    const toggleSelectedState = (index: number) => {
        setSelectedStates((prev) => {
            const newSelectedStates = [...prev];
            newSelectedStates[index] = newSelectedStates[index] === 0 ? 1 : 0;
            return newSelectedStates;
        });
    };

    const [movie1, setMovie1] = useState<string | null>(null);
    const [movie2, setMovie2] = useState<string | null>(null);

    return (
        <>
            <YStack gap={16} maxWidth={500}>
                <BodyLargeEmphasized>RadioInputRow</BodyLargeEmphasized>
                <SegmentedControl
                    selected={disabled ? 'true' : 'false'}
                    defaultValue={disabled ? 'true' : 'false'}
                    onValueChange={(v) => {
                        setDisabled(v === 'true');
                    }}
                    options={[
                        { label: 'Enabled', value: 'false' },
                        { label: 'Disabled', value: 'true' },
                    ]}
                    size="small"
                    width="auto"
                />
                <YStack>
                    <RadioInputRow
                        label="RadioInputRow 1"
                        isSelected={selectedStates[0] === 1}
                        onPress={() => {
                            toggleSelectedState(0);
                            toggleSelectedState(1);
                        }}
                        disabled={disabled}
                    />
                    <RadioInputRow
                        label="RadioInputRow 2"
                        details="With detail text here"
                        isSelected={selectedStates[1] === 1}
                        onPress={() => {
                            toggleSelectedState(1);
                            toggleSelectedState(0);
                        }}
                        disabled={disabled}
                    />
                    <RadioInputRow
                        tone="positive"
                        labelFontWeight="regular"
                        label="RadioInputRow (classic styles) 1"
                        isSelected={selectedStates[2] === 1}
                        onPress={() => {
                            toggleSelectedState(2);
                            toggleSelectedState(3);
                        }}
                        disabled={disabled}
                    />
                    <RadioInputRow
                        tone="positive"
                        labelFontWeight="regular"
                        label="RadioInputRow (classic styles) 2"
                        labelIcon={CarIcon}
                        isSelected={selectedStates[3] === 1}
                        onPress={() => {
                            toggleSelectedState(3);
                            toggleSelectedState(2);
                        }}
                        disabled={disabled}
                    />
                    <RadioInputRow
                        label="RadioInputRow with leftAlignOnWideScreen"
                        isSelected={selectedStates[4] === 1}
                        onPress={() => {
                            toggleSelectedState(4);
                            toggleSelectedState(5);
                        }}
                        leftAlignOnWideScreen
                        disabled={disabled}
                    />
                    <RadioInputRow
                        label="RadioInputRow with leftAlignOnWideScreen"
                        isSelected={selectedStates[5] === 1}
                        onPress={() => {
                            toggleSelectedState(4);
                            toggleSelectedState(5);
                        }}
                        leftAlignOnWideScreen
                        disabled={disabled}
                    />
                    <RadioInputRow
                        label="RadioInputRow - select to see details"
                        labelIcon={RabbitIcon}
                        detailsHiddenUntilSelected={true}
                        details={
                            <YStack flex={1}>
                                <BodySmallRegular>
                                    detailsHiddenUntilSelected = true
                                </BodySmallRegular>
                                <Input
                                    marginTop={12}
                                    placeholder="Neat"
                                    enterKeyHint="done"
                                    disabled={disabled}
                                />
                            </YStack>
                        }
                        isSelected={selectedStates[6] === 1}
                        onPress={() => {
                            toggleSelectedState(6);
                            toggleSelectedState(7);
                        }}
                        disabled={disabled}
                    />
                    <RadioInputRow
                        label="RadioInputRow - custom details always shown"
                        details={
                            <Input
                                value="20"
                                marginTop={12}
                                placeholder="Enter an amount"
                                enterKeyHint="done"
                                disabled={disabled || selectedStates[7] !== 1}
                            />
                        }
                        isSelected={selectedStates[7] === 1}
                        onPress={() => {
                            toggleSelectedState(7);
                            toggleSelectedState(6);
                        }}
                        disabled={disabled}
                    />
                </YStack>
            </YStack>
            <YStack gap={16} maxWidth={500}>
                <BodyLargeEmphasized>
                    RadioInputRowList (default style, and classic style)
                </BodyLargeEmphasized>
                <RadioInputRowList
                    items={[
                        'The Fast and the Furious',
                        '2 Fast 2 Furious',
                        'The Fast and the Furious: Tokyo Drift',
                    ].map((value, index) => ({
                        label: value,
                        isSelected:
                            movie1 !== null ? value === movie1 : index === 0,
                        onPress: () => {
                            setMovie1(value);
                        },
                        disabled,
                    }))}
                />
                <RadioInputRowList
                    hasSeparators={false}
                    items={[
                        'Transformers',
                        'Transformers: Revenge of the Fallen',
                        'Transformers: Dark of the Moon',
                    ].map((value, index) => ({
                        fontWeight: 'regular',
                        tone: 'positive',
                        label: value,
                        isSelected:
                            movie2 !== null ? value === movie2 : index === 0,
                        onPress: () => {
                            setMovie2(value);
                        },
                        disabled,
                    }))}
                />
            </YStack>
        </>
    );
};
